import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Box, Button, Card, CircularProgress, Grid, Switch, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { getCollectionsSelectedLocation } from '../../navigation/components/CollectionsLocationToggle';
import { useCollectionClient } from '../../clients/hooks/useCollectionClient';
import { useCollectionClientNotes } from '../../clients/hooks/useCollectionClientNotes';
import ClientCompanyInfoCard from '../../clients/components/ClientCompanyInfoCard';
import ClientRecentNotesCard from '../../clients/components/ClientRecentNotesCard';
import ClientEmailHistoryTable from '../../clients/components/ClientEmailHistoryTable';
import CollectionClientNotesModal from '../../clients/components/CollectionClientNotesModal';
import { SectionLabel } from '../../clients/components/clientDetailPresentation';
import { CollectionInvoicePdf } from '../../clients/model';
import { StatementInvoice } from '../model';
import { useStatement } from '../hooks/useStatement';
import { useUpdateStatementEnabled } from '../hooks/useUpdateStatementEnabled';
import { statementsQueryKey, STATEMENTS_URL } from '../hooks/useStatements';
import StatementInvoicesTable from '../components/StatementInvoicesTable';

// Company info / notes / email history need a linked collections client. That link is only
// transiently absent (a new invoice account before the nightly upsert), so we render a small
// placeholder rather than hiding the whole page.
const SyncingPlaceholder: React.FC<{ label: string }> = ({ label }) => (
  <>
    <SectionLabel>{label}</SectionLabel>
    <Card sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary">
        Client details are syncing and will appear after the nightly import.
      </Typography>
    </Card>
  </>
);

const ClientSectionPlaceholder: React.FC<{ label: string; isLoading: boolean; isError: boolean }> = ({
  label,
  isLoading,
  isError,
}) => {
  if (isLoading) {
    return (
      <>
        <SectionLabel>{label}</SectionLabel>
        <Card sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={24} />
        </Card>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <SectionLabel>{label}</SectionLabel>
        <Card sx={{ p: 3 }}>
          <Alert severity="error">Failed to load client details.</Alert>
        </Card>
      </>
    );
  }

  return <SyncingPlaceholder label={label} />;
};

const StatementDetailPage: React.FC = () => {
  const { acctNo: acctNoParam } = useParams<{ acctNo: string }>();
  const acctNo = decodeURIComponent(acctNoParam);
  const history = useHistory();
  const queryClient = useQueryClient();
  const [location] = useState(() => getCollectionsSelectedLocation());
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);

  const { data, isLoading, isError } = useStatement(acctNo, location);
  const statement = data?.data ?? null;
  const collectionsClientId = statement?.collections_client_id ?? null;

  const { data: clientData, isLoading: isClientLoading, isError: isClientError } = useCollectionClient(collectionsClientId);
  const client = clientData?.data ?? null;
  const {
    data: notesData,
    isLoading: isNotesLoading,
    isError: isNotesError,
  } = useCollectionClientNotes(collectionsClientId);
  const notes = notesData?.data ?? [];

  const { updateStatementEnabled, isLoading: isToggling } = useUpdateStatementEnabled();

  const [enabled, setEnabled] = useState(false);
  const [invoices, setInvoices] = useState<StatementInvoice[]>([]);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    if (statement) {
      setEnabled(statement.statement_enabled);
      setInvoices(statement.invoices);
    }
  }, [statement]);

  const handleToggle = async (checked: boolean) => {
    const previous = enabled;
    setEnabled(checked);
    try {
      await updateStatementEnabled({ acctNo, location, statementEnabled: checked });
      void queryClient.invalidateQueries({ queryKey: statementsQueryKey(location) });
      void queryClient.invalidateQueries({ queryKey: [`${STATEMENTS_URL}/${acctNo}`, { location }] });
      showSuccessSnackBar?.(`Statement ${checked ? 'enabled' : 'disabled'}.`);
    } catch (error: any) {
      setEnabled(previous);
      const serverMessage = error?.response?.data?.errors?.[0] || error?.response?.data?.error;
      showErrorSnackBar?.(serverMessage || 'Failed to update statement status.');
    }
  };

  const handleUploaded = (applyTo: string, pdf: CollectionInvoicePdf) => {
    setInvoices((prev) => prev.map((i) => (i.apply_to === applyTo ? { ...i, has_pdf: true, pdf } : i)));
    showSuccessSnackBar?.('Invoice PDF uploaded.');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !statement) {
    return (
      <Grid container>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Alert severity="error">Failed to load statement.</Alert>
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => history.push('/collections/statements')}>
            Back to Statements
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle={`Statements — ${statement.company}`} titleIcon={<DescriptionIcon />} />
      </Grid>

      <Grid
        item
        xs={12}
        sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}
      >
        <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => history.push('/collections/statements')}>
          Back to Statements
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2">Statement Enabled</Typography>
          <Switch
            checked={enabled}
            onChange={(e) => handleToggle(e.target.checked)}
            color="primary"
            size="small"
            disabled={isToggling || !collectionsClientId}
            inputProps={{ 'aria-label': `Statement enabled for ${statement.company}` }}
          />
        </Box>
      </Grid>

      <Grid item xs={12} sx={{ mt: 2.5 }}>
        <SectionLabel>Statement Invoices</SectionLabel>
        <StatementInvoicesTable invoices={invoices} location={location} acctNo={acctNo} onUploaded={handleUploaded} />
      </Grid>

      <Grid item xs={12} sx={{ mt: 2 }}>
        <Box
          sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '65fr 35fr' }, gap: 2, alignItems: 'stretch' }}
        >
          {collectionsClientId && client ? (
            <ClientCompanyInfoCard
              client={client}
              clientId={collectionsClientId}
              readOnly
              recipientEmailsLabel="Statement emails sent to:"
              contactsOverride={client.statement_contacts ?? []}
              recipientEmails={client.statement_emails ?? []}
              recipientEmailsDisplay={client.statement_emails ?? []}
              allowBackupInRecipientEmptyState={false}
            />
          ) : (
            <ClientSectionPlaceholder
              label="Company Info"
              isLoading={!!collectionsClientId && isClientLoading}
              isError={!!collectionsClientId && isClientError}
            />
          )}
          {collectionsClientId && !isNotesLoading && !isNotesError ? (
            <ClientRecentNotesCard notes={notes} onViewAll={() => setNotesOpen(true)} />
          ) : (
            <ClientSectionPlaceholder
              label="Recent Notes"
              isLoading={!!collectionsClientId && isNotesLoading}
              isError={!!collectionsClientId && isNotesError}
            />
          )}
        </Box>
      </Grid>

      <Grid item xs={12} sx={{ mt: 2.5 }}>
        <SectionLabel>Email History</SectionLabel>
        {collectionsClientId ? (
          <ClientEmailHistoryTable location={location} clientId={collectionsClientId} emailType="statement" />
        ) : (
          <Card sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Client details are syncing and will appear after the nightly import.
            </Typography>
          </Card>
        )}
      </Grid>

      {collectionsClientId && (
        <CollectionClientNotesModal
          open={notesOpen}
          onClose={() => setNotesOpen(false)}
          clientId={collectionsClientId}
          company={statement.company}
          onNotesChanged={() => {
            void queryClient.invalidateQueries({
              queryKey: [`/collections/clients/${collectionsClientId}/notes`, undefined],
            });
          }}
        />
      )}
    </Grid>
  );
};

export default StatementDetailPage;
